import { getCitationUrl } from '@/utils/jsonTyping';
import { InsightItemDisplay, parseInsightsFromJson } from '@/components/InsightItemDisplay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Copy,
  Heart,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  MoreVertical,
  Trash2,
  FileEdit,
  MonitorPlay,
  X,
  Sparkles,
  TrendingUp,
  Quote,
  Type,
  Lightbulb,
  Wand2,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ContentIdea } from '@/types/dashboard';
import { useFavorites } from '@/hooks/useFavorites';
import { useMarkAsUsed } from '@/hooks/useMarkAsUsed';
import { useState, useEffect, useRef, MouseEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CorkModernModalProps {
  idea: ContentIdea | null;
  isOpen: boolean;
  onClose: () => void;
  onCopyScript: (content: string) => void;
  onFavoriteToggle?: () => void;
  onUsedToggle?: () => void;
  onTrash?: (id: string, source: 'scraped' | 'user_generated') => void;
}

// 21st.dev style - Spotlight Bento Card for carousel items
const SpotlightCard = ({ 
  children, 
  className 
}: { 
  children: React.ReactNode; 
  className?: string;
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setOpacity(1)}
      onMouseLeave={() => setOpacity(0)}
      className={cn(
        "relative overflow-hidden rounded-2xl",
        "bg-white dark:bg-white/5",
        "border border-neutral-200 dark:border-white/10",
        className
      )}
    >
      <div
        className="pointer-events-none absolute -inset-px transition-opacity duration-300"
        style={{
          opacity,
          background: `radial-gradient(400px circle at ${position.x}px ${position.y}px, rgba(99,102,241,0.1), transparent 40%)`,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

// Modern Carousel with 21st.dev styling
interface ModernCarouselProps {
  items: string[];
  currentIndex: number;
  onChange: (index: number) => void;
  icon: React.ReactNode;
  title: string;
  renderItem?: (item: string) => React.ReactNode;
}

const ModernCarousel = ({ 
  items, 
  currentIndex, 
  onChange, 
  icon, 
  title,
  renderItem 
}: ModernCarouselProps) => {
  return (
    <SpotlightCard>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-neutral-100 dark:bg-white/5 flex items-center justify-center">
              {icon}
            </div>
            <h3 className="text-sm font-semibold">{title}</h3>
          </div>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {currentIndex + 1}/{items.length}
          </span>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="min-h-[60px] flex items-center"
          >
            {renderItem ? renderItem(items[currentIndex]) : (
              <p className="text-sm leading-relaxed">{items[currentIndex]}</p>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-3 mt-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onChange(currentIndex - 1)}
            disabled={currentIndex === 0}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              "bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10",
              "transition-colors",
              currentIndex === 0 
                ? "opacity-40 cursor-not-allowed" 
                : "hover:border-primary/30 hover:bg-primary/5"
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </motion.button>

          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => onChange(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === currentIndex 
                    ? "w-5 bg-primary" 
                    : "w-1.5 bg-neutral-300 dark:bg-white/20 hover:bg-neutral-400"
                )}
              />
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onChange(currentIndex + 1)}
            disabled={currentIndex === items.length - 1}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              "bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10",
              "transition-colors",
              currentIndex === items.length - 1 
                ? "opacity-40 cursor-not-allowed" 
                : "hover:border-primary/30 hover:bg-primary/5"
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </motion.button>
        </div>
      </div>
    </SpotlightCard>
  );
};

// Compact Viral Score Badge for sidebar
const ViralScoreBadge = ({ score }: { score: number }) => {
  const getConfig = (s: number) => {
    if (s >= 70) return { 
      color: 'text-emerald-600 dark:text-emerald-400', 
      bg: 'bg-emerald-50 dark:bg-emerald-950/30',
      border: 'border-emerald-200 dark:border-emerald-800',
      label: 'Wysoki potencjał',
      bar: 'bg-emerald-500'
    };
    if (s >= 50) return { 
      color: 'text-amber-600 dark:text-amber-400', 
      bg: 'bg-amber-50 dark:bg-amber-950/30',
      border: 'border-amber-200 dark:border-amber-800',
      label: 'Średni potencjał',
      bar: 'bg-amber-500'
    };
    return { 
      color: 'text-slate-600 dark:text-slate-400', 
      bg: 'bg-slate-50 dark:bg-slate-950/30',
      border: 'border-slate-200 dark:border-slate-800',
      label: 'Niski potencjał',
      bar: 'bg-slate-400'
    };
  };

  const config = getConfig(score);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "p-3 rounded-xl",
        "bg-white dark:bg-white/5",
        "border border-neutral-200 dark:border-white/10"
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center",
          config.bg,
          config.border,
          "border"
        )}>
          <TrendingUp className={cn("h-4 w-4", config.color)} />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-1">
            <span className={cn("text-xl font-bold", config.color)}>
              {score.toFixed(1)}
            </span>
            <span className="text-xs text-neutral-400">/100</span>
          </div>
          <p className={cn("text-[10px] font-medium", config.color)}>
            {config.label}
          </p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-2 h-1 bg-neutral-100 dark:bg-white/10 rounded-full overflow-hidden">
        <motion.div 
          className={cn("h-full rounded-full", config.bar)}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
};

export const CorkModernModal = ({
  idea,
  isOpen,
  onClose,
  onCopyScript,
  onFavoriteToggle,
  onUsedToggle,
  onTrash
}: CorkModernModalProps) => {
  const navigate = useNavigate();
  const { toggleFavorite, isLoading: isFavoriteLoading } = useFavorites();
  const { toggleUsed, isLoading: isUsedLoading } = useMarkAsUsed();
  
  const [localIsFavorited, setLocalIsFavorited] = useState(idea?.is_favorited || false);
  const [localIsUsed, setLocalIsUsed] = useState(idea?.is_used || false);
  
  const [hookCurrent, setHookCurrent] = useState(0);
  const [overlayCurrent, setOverlayCurrent] = useState(0);
  const [insightsCurrent, setInsightsCurrent] = useState(0);
  const [improvementsCurrent, setImprovementsCurrent] = useState(0);

  // Mobile collapsible sections
  const [isScriptExpanded, setIsScriptExpanded] = useState(false);
  const [isInsightsExpanded, setIsInsightsExpanded] = useState(false);
  const [isImprovementsExpanded, setIsImprovementsExpanded] = useState(false);

  // Reset state when modal opens with new idea
  useEffect(() => {
    if (isOpen && idea) {
      setLocalIsFavorited(idea.is_favorited || false);
      setLocalIsUsed(idea.is_used || false);
      setHookCurrent(0);
      setOverlayCurrent(0);
      setInsightsCurrent(0);
      setImprovementsCurrent(0);
      setIsScriptExpanded(false);
      setIsInsightsExpanded(false);
      setIsImprovementsExpanded(false);
    }
  }, [isOpen, idea?.id]);

  if (!idea) return null;

  const getTableName = (): 'content_ideas' | 'user_generated_content' => {
    return idea.source === 'user_generated' ? 'user_generated_content' : 'content_ideas';
  };

  const handleNavigateToEditor = () => {
    onClose();
    navigate(`/edytor?id=${idea.id}&table=${getTableName()}`);
  };

  const handleNavigateToPrompter = () => {
    onClose();
    navigate(`/prompter?id=${idea.id}&table=${getTableName()}`);
  };

  // Safely get array values from potentially non-array fields (handles Json type)
  const getArrayValue = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(item => String(item));
      } catch {
        return [value];
      }
    }
    return [];
  };

  const parseImprovements = (improvements: unknown): string[] => {
    if (!improvements) return [];
    if (Array.isArray(improvements)) return improvements.map(imp => String(imp));
    if (typeof improvements === 'string') {
      const trimmed = improvements.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.map(imp => String(imp));
        } catch {
          return [trimmed];
        }
      }
      return [trimmed];
    }
    return [];
  };

  const parsePerplexityInsights = (data: unknown) => parseInsightsFromJson(data);

  const getCitationsFromInsights = (data: unknown): string[] => {
    if (!data) return [];
    let parsed = data;
    if (typeof data === 'string') {
      try { parsed = JSON.parse(data); } catch { return []; }
    }
    if (parsed && typeof parsed === 'object' && 'citations' in parsed) {
      const citations = (parsed as { citations: unknown }).citations;
      if (Array.isArray(citations)) return citations.map(String);
    }
    return [];
  };

  const altHooks = getArrayValue(idea.alt_hooks);
  const textOverlays = getArrayValue(idea.text_overlays);
  const improvementsApplied = parseImprovements(idea.improvements_applied);
  const perplexityInsights = parsePerplexityInsights(idea.perplexity_insights);
  const citations = getCitationsFromInsights(idea.perplexity_insights);

  const getScriptContent = (content: string | null): string => {
    if (!content) return 'Brak treści scenariusza';
    
    // First, unescape any literal \n sequences to actual newlines
    const normalizeNewlines = (text: string): string => {
      return text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/\\n\\n/g, '\n\n')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };
    
    try {
      const parsed = JSON.parse(content);
      const script = parsed.script || content;
      return normalizeNewlines(script);
    } catch {
      return normalizeNewlines(content);
    }
  };

  const scriptContent = getScriptContent(idea.edited_content || idea.content);

  // Use shared citation URL resolver — imported from jsonTyping

  const getVideoUrl = () => {
    return idea.source_competitor?.video_url || `https://instagram.com/${idea.source_competitor?.username?.replace('@', '') || ''}`;
  };

  const handleFavoriteToggle = async () => {
    setLocalIsFavorited(!localIsFavorited);
    const success = await toggleFavorite(idea.id, localIsFavorited, idea.source);
    if (success && onFavoriteToggle) {
      onFavoriteToggle();
    } else if (!success) {
      setLocalIsFavorited(localIsFavorited);
    }
  };

  const handleUsedToggle = async () => {
    setLocalIsUsed(!localIsUsed);
    const success = await toggleUsed(idea.id, localIsUsed, idea.source);
    if (success && onUsedToggle) {
      onUsedToggle();
    } else if (!success) {
      setLocalIsUsed(localIsUsed);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] md:max-w-[90vw] lg:max-w-[1100px] h-[90dvh] md:h-[85vh] max-h-[90dvh] p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border border-neutral-200 dark:border-white/10 rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle className="sr-only">Szczegóły pomysłu</DialogTitle>
          <DialogDescription className="sr-only">{idea.title}</DialogDescription>
        </DialogHeader>

        {/* Close button - top left */}
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            "absolute top-4 left-4 z-50",
            "h-10 w-10 rounded-xl",
            "bg-white dark:bg-white/10",
            "border border-neutral-200 dark:border-white/10",
            "flex items-center justify-center",
            "hover:border-neutral-300 dark:hover:border-white/20",
            "transition-colors duration-200",
            "shadow-sm"
          )}
          aria-label="Zamknij"
        >
          <X className="w-5 h-5 text-foreground" />
        </motion.button>

        {/* Desktop Layout - Two column flex */}
        <div className="hidden md:flex h-full overflow-hidden">
          {/* Left Sidebar - Scrollable */}
          <div className="w-[380px] border-r border-neutral-200 dark:border-white/10 bg-neutral-50/50 dark:bg-white/[0.02] h-full flex flex-col">
            <ScrollArea className="flex-1">
              <div className="p-5 pt-16 space-y-5">
              {/* Viral Score & Title */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {idea.viral_score && (
                  <ViralScoreBadge score={idea.viral_score} />
                )}
                
                <h2 className="text-xl font-bold text-foreground leading-snug">
                  {idea.title}
                </h2>
                {idea.description && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">{idea.description}</p>
                )}
              </motion.div>

              <Separator className="bg-neutral-200 dark:bg-white/10" />

              {/* Hook Section */}
              <AnimatePresence>
                {altHooks.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <ModernCarousel
                      items={altHooks}
                      currentIndex={hookCurrent}
                      onChange={setHookCurrent}
                      icon={<Quote className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                      title="Hook"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Text Overlays */}
              <AnimatePresence>
                {textOverlays.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <ModernCarousel
                      items={textOverlays}
                      currentIndex={overlayCurrent}
                      onChange={setOverlayCurrent}
                      icon={<Type className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                      title="Nakładki tekstowe"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Insights */}
              <AnimatePresence>
                {perplexityInsights.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <ModernCarousel
                      items={perplexityInsights.map((_, idx) => String(idx))}
                      currentIndex={insightsCurrent}
                      onChange={setInsightsCurrent}
                      icon={<Lightbulb className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                      title="Ciekawostki"
                      renderItem={(idxStr) => {
                        const insight = perplexityInsights[parseInt(idxStr)];
                        if (!insight) return null;
                        const citationUrl = getCitationUrl(insight.source, citations);
                        return <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} />;
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Improvements */}
              <AnimatePresence>
                {improvementsApplied.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <ModernCarousel
                      items={improvementsApplied}
                      currentIndex={improvementsCurrent}
                      onChange={setImprovementsCurrent}
                      icon={<Wand2 className="h-4 w-4 text-violet-600 dark:text-violet-400" />}
                      title="Zastosowane ulepszenia"
                      renderItem={(item) => (
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-violet-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm">{item}</p>
                        </div>
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bottom spacer */}
              <div className="h-4" />
              </div>
            </ScrollArea>
          </div>

          {/* Right Content - Script - Scrollable */}
          <div className="flex-1 h-full flex flex-col bg-white dark:bg-transparent">
            {/* Script Header with All Buttons */}
            <div className="p-5 border-b border-neutral-200 dark:border-white/10 flex items-center justify-between bg-neutral-50/30 dark:bg-white/[0.02] shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-bold text-lg">Scenariusz</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => onCopyScript(scriptContent)} variant="outline" size="sm" className="gap-2 rounded-xl">
                  <Copy className="h-4 w-4" />
                  Kopiuj
                </Button>
                <Button onClick={handleNavigateToEditor} variant="outline" size="sm" className="gap-2 rounded-xl">
                  <FileEdit className="h-4 w-4" />
                  Edytuj
                </Button>
                <Button onClick={handleNavigateToPrompter} variant="outline" size="sm" className="gap-2 rounded-xl bg-primary/10 border-primary/30 hover:bg-primary/20">
                  <MonitorPlay className="h-4 w-4" />
                  Prompter
                </Button>
              </div>
            </div>

            {/* Script Content - Scrollable */}
            <ScrollArea className="flex-1">
              <div className="p-5">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="p-6 rounded-2xl bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/10"
                >
                  <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap text-foreground/90">
                    {scriptContent}
                  </p>
                </motion.div>
              </div>
            </ScrollArea>

            {/* Footer Actions */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 border-t border-neutral-200 dark:border-white/10 bg-neutral-50/50 dark:bg-white/[0.02] shrink-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFavoriteToggle}
                    disabled={isFavoriteLoading}
                    className={cn(
                      "gap-2 rounded-xl",
                      localIsFavorited && "text-red-500 border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800"
                    )}
                  >
                    <Heart className={cn("h-4 w-4", localIsFavorited && "fill-red-500")} />
                    {localIsFavorited ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUsedToggle}
                    disabled={isUsedLoading}
                    className={cn(
                      "gap-2 rounded-xl",
                      localIsUsed && "text-green-600 border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800"
                    )}
                  >
                    <CheckCircle2 className={cn("h-4 w-4", localIsUsed && "fill-green-100")} />
                    {localIsUsed ? 'Oznacz jako nieużyty' : 'Oznacz jako użyty'}
                  </Button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-xl" aria-label="Więcej opcji">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuItem onClick={() => onTrash?.(idea.id, idea.source || 'scraped')} className="text-destructive focus:text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Przenieś do kosza
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden w-full h-full overflow-hidden flex flex-col">
          {/* Mobile Header with Icon Buttons */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-white/10 shrink-0">
            <motion.button
              onClick={onClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="h-10 w-10 rounded-xl bg-white dark:bg-white/10 border border-neutral-200 dark:border-white/10 flex items-center justify-center"
              aria-label="Zamknij"
            >
              <X className="w-5 h-5" />
            </motion.button>
            <div className="flex items-center gap-2">
              <Button onClick={() => onCopyScript(scriptContent)} variant="outline" size="icon" className="h-10 w-10 rounded-xl" aria-label="Kopiuj skrypt">
                <Copy className="h-4 w-4" />
              </Button>
              <Button onClick={handleNavigateToEditor} variant="outline" size="icon" className="h-10 w-10 rounded-xl" aria-label="Otwórz w edytorze">
                <FileEdit className="h-4 w-4" />
              </Button>
              <Button onClick={handleNavigateToPrompter} variant="outline" size="icon" className="h-10 w-10 rounded-xl bg-primary/10 border-primary/30" aria-label="Otwórz w prompterze">
                <MonitorPlay className="h-4 w-4" />
              </Button>
              <Button onClick={handleFavoriteToggle} variant="outline" size="icon" disabled={isFavoriteLoading} className={cn("h-10 w-10 rounded-xl", localIsFavorited && "text-red-500 border-red-200 bg-red-50")} aria-label={localIsFavorited ? "Usuń z ulubionych" : "Dodaj do ulubionych"}>
                <Heart className={cn("h-4 w-4", localIsFavorited && "fill-red-500")} />
              </Button>
            </div>
          </div>

          {/* Mobile Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Viral Score & Title */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              {idea.viral_score && <ViralScoreBadge score={idea.viral_score} />}
              <h2 className="text-xl font-bold text-foreground leading-tight">{idea.title}</h2>
              {idea.description && <p className="text-sm text-neutral-600 dark:text-neutral-400">{idea.description}</p>}
            </motion.div>

            <Separator className="bg-neutral-200 dark:bg-white/10" />

            {/* Hook */}
            {altHooks.length > 0 && (
              <ModernCarousel items={altHooks} currentIndex={hookCurrent} onChange={setHookCurrent} icon={<Quote className="h-4 w-4 text-blue-600" />} title="Hook" />
            )}

            {/* Text Overlays */}
            {textOverlays.length > 0 && (
              <ModernCarousel items={textOverlays} currentIndex={overlayCurrent} onChange={setOverlayCurrent} icon={<Type className="h-4 w-4 text-orange-600" />} title="Nakładki tekstowe" />
            )}

            {/* Script - Collapsible */}
            <SpotlightCard>
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold">Scenariusz</h3>
                  </div>
                  <span className="text-xs text-neutral-400">{isScriptExpanded ? 'Pokaż mniej' : 'Pokaż więcej'}</span>
                </div>
                <div 
                  className="p-4 rounded-xl bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/10 cursor-pointer"
                  onClick={() => setIsScriptExpanded(!isScriptExpanded)}
                >
                  <p className={cn(
                    "text-sm leading-relaxed whitespace-pre-wrap text-foreground/90",
                    !isScriptExpanded && "line-clamp-5"
                  )}>
                    {scriptContent}
                  </p>
                </div>
              </div>
            </SpotlightCard>

            {/* Insights - Collapsible */}
            {perplexityInsights.length > 0 && (
              <SpotlightCard>
                <div className="p-4">
                  <button 
                    onClick={() => setIsInsightsExpanded(!isInsightsExpanded)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Lightbulb className="h-4 w-4 text-emerald-600" />
                      </div>
                      <h3 className="text-sm font-semibold">Ciekawostki</h3>
                      <span className="text-xs text-neutral-400">({perplexityInsights.length})</span>
                    </div>
                    {isInsightsExpanded ? <ChevronRight className="h-4 w-4 rotate-90 transition-transform" /> : <ChevronRight className="h-4 w-4 transition-transform" />}
                  </button>
                  <AnimatePresence>
                    {isInsightsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 mt-3 border-t border-neutral-200 dark:border-white/10">
                          <ModernCarousel
                            items={perplexityInsights.map((_, idx) => String(idx))}
                            currentIndex={insightsCurrent}
                            onChange={setInsightsCurrent}
                            icon={<Lightbulb className="h-4 w-4 text-emerald-600" />}
                            title=""
                            renderItem={(idxStr) => {
                              const insight = perplexityInsights[parseInt(idxStr)];
                              if (!insight) return null;
                              const citationUrl = getCitationUrl(insight.source, citations);
                              return <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} compact />;
                            }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </SpotlightCard>
            )}

            {/* Improvements - Collapsible */}
            {improvementsApplied.length > 0 && (
              <SpotlightCard>
                <div className="p-4">
                  <button 
                    onClick={() => setIsImprovementsExpanded(!isImprovementsExpanded)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
                        <Wand2 className="h-4 w-4 text-violet-600" />
                      </div>
                      <h3 className="text-sm font-semibold">Ulepszenia</h3>
                      <span className="text-xs text-neutral-400">({improvementsApplied.length})</span>
                    </div>
                    {isImprovementsExpanded ? <ChevronRight className="h-4 w-4 rotate-90 transition-transform" /> : <ChevronRight className="h-4 w-4 transition-transform" />}
                  </button>
                  <AnimatePresence>
                    {isImprovementsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 mt-3 border-t border-neutral-200 dark:border-white/10">
                          <ModernCarousel
                            items={improvementsApplied}
                            currentIndex={improvementsCurrent}
                            onChange={setImprovementsCurrent}
                            icon={<Wand2 className="h-4 w-4 text-violet-600" />}
                            title=""
                            renderItem={(item) => (
                              <div className="flex items-start gap-2">
                                <CheckCircle2 className="h-4 w-4 text-violet-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm">{item}</p>
                              </div>
                            )}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </SpotlightCard>
            )}


            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-neutral-200 dark:border-white/10">
              <Button onClick={() => onCopyScript(scriptContent)} className="flex-1" size="sm">
                <Copy className="mr-2 h-4 w-4" />Kopiuj
              </Button>
              <Button variant="outline" size="sm" onClick={handleUsedToggle} disabled={isUsedLoading} aria-label={localIsUsed ? "Oznacz jako nieużyte" : "Oznacz jako użyte"} className={cn(localIsUsed && "text-green-600")}>
                <CheckCircle2 className={cn("h-4 w-4", localIsUsed && "fill-green-100")} />
              </Button>
              <Button variant="outline" size="sm" onClick={handleFavoriteToggle} disabled={isFavoriteLoading} aria-label={localIsFavorited ? "Usuń z ulubionych" : "Dodaj do ulubionych"} className={cn(localIsFavorited && "text-red-500")}>
                <Heart className={cn("h-4 w-4", localIsFavorited && "fill-red-500")} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" aria-label="Więcej opcji"><MoreVertical className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleNavigateToEditor}><FileEdit className="mr-2 h-4 w-4" />Edytuj</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTrash?.(idea.id, idea.source || 'scraped')} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Do kosza</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="h-4" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CorkModernModal;
